import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, Article, Tag as TagType } from '../utils/api';
import { ArticleCard, CardSize } from '../components/ArticleCard';
import { Pagination } from '../components/Pagination';
import { SEO } from '../components/SEO';
import { seoConfig } from '../config/seo';

const CARD_SIZE_KEY = 'bytejourney-card-size';

export function Tag() {
  const { tagName } = useParams<{ tagName: string }>();
  const [articles, setArticles] = useState<Article[]>([]);
  const [allTags, setAllTags] = useState<TagType[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [cardSize, setCardSize] = useState<CardSize>(() => {
    const saved = localStorage.getItem(CARD_SIZE_KEY);
    return (saved as CardSize) || 'large';
  });
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  });

  const decodedTag = tagName ? decodeURIComponent(tagName) : '';

  const handleCardSizeChange = (size: CardSize) => {
    setCardSize(size);
    localStorage.setItem(CARD_SIZE_KEY, size);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!decodedTag) return;

      setLoading(true);
      try {
        const [articlesRes, tagsRes] = await Promise.all([
          api.getArticles({ page, limit: 10, tag: decodedTag }),
          api.getTags(),
        ]);

        setArticles(articlesRes.articles);
        setPagination({
          page: articlesRes.pagination.page,
          totalPages: articlesRes.pagination.totalPages,
          total: articlesRes.pagination.total,
        });
        setAllTags(tagsRes.tags);
      } catch (error) {
        console.error('Failed to fetch articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [decodedTag, page]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // SEO metadata
  const tagTitle = `#${decodedTag} Articles`;
  const tagDescription = `Browse all articles tagged with #${decodedTag} on ${seoConfig.siteName}. Discover insights on ${decodedTag} and related topics.`;

  return (
    <>
      <SEO
        title={tagTitle}
        description={tagDescription}
        canonical={`/tag/${encodeURIComponent(decodedTag)}`}
        ogType="website"
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2">
            <li>
              <Link to="/" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                Home
              </Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 dark:text-gray-100 font-medium">
              #{decodedTag}
            </li>
          </ol>
        </nav>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main content */}
          <div className="flex-1">
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  #{decodedTag}
                </h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1">
                  {pagination.total} {pagination.total === 1 ? 'article' : 'articles'}
                </p>
              </div>

              {/* Card size toggle */}
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                <button
                  onClick={() => handleCardSizeChange('large')}
                  className={`p-1.5 rounded-md transition-colors ${
                    cardSize === 'large'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                  title="Large cards"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleCardSizeChange('compact')}
                  className={`p-1.5 rounded-md transition-colors ${
                    cardSize === 'compact'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                  title="Compact list"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner w-8 h-8" />
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400 text-lg">No articles found with this tag</p>
                <Link
                  to="/"
                  className="mt-4 inline-block text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                >
                  View all articles
                </Link>
              </div>
            ) : (
              <>
                <div className={cardSize === 'large' ? 'grid gap-6 md:grid-cols-2' : 'flex flex-col gap-4'}>
                  {articles.map((article) => (
                    <ArticleCard key={article.id} article={article} size={cardSize} />
                  ))}
                </div>

                <div className="mt-8">
                  <Pagination
                    page={pagination.page}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              </>
            )}
          </div>

          {/* Sidebar - Related Tags */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="card p-5 sticky top-24">
              <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-4">All Tags</h3>
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (
                  <Link
                    key={tag.name}
                    to={`/tag/${encodeURIComponent(tag.name)}`}
                    className={`badge transition-colors ${
                      tag.name === decodedTag
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    #{tag.name}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}
